import React, { useState, useEffect, useRef } from 'react';
import { EmailService, ApplicationData } from '../services/emailService';
import AddressAutocomplete from './AddressAutocomplete';
import SimpleCaptcha from './SimpleCaptcha';

interface ApplicationWorkflowProps {
  onBackToHome: () => void;
  prefillData?: {
    vehicleType?: string;
    monthlyBudget?: string;
  };
}

const ApplicationWorkflow: React.FC<ApplicationWorkflowProps> = ({ onBackToHome, prefillData }) => {
  const [currentStep, setCurrentStep] = useState(1);
  
  const [isAutoAdvancing, setIsAutoAdvancing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [captchaValid, setCaptchaValid] = useState(false);
  
  // Refs for auto-focusing input fields
  const firstNameRef = useRef<HTMLInputElement>(null);
  const dateOfBirthRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const phoneRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    vehicleType: prefillData?.vehicleType || '',
    monthlyBudget: prefillData?.monthlyBudget || '',
    hasTradeIn: '',
    employmentStatus: '',
    monthlyIncome: '',
    address: '',
    firstName: '',
    dateOfBirth: '',
    email: '',
    phone: '',
    // Honeypot fields to catch bots
    website: '', // Hidden field - bots often fill this
    confirmEmail: '' // Hidden field - should remain empty
  });

  const totalSteps = 10;

  // Estimated time per step (in minutes) - Total: 2.7 minutes
  const stepTimes = {
    1: 0.3,   // Vehicle selection
    2: 0.3,   // Monthly budget
    3: 0.3,   // Trade-in question
    4: 0.3,   // Employment status
    5: 0.3,   // Income
    6: 0.3,   // Address
    7: 0.3,   // Name
    8: 0.3,   // Date of birth
    9: 0.3,   // Email
    10: 0.3   // Phone
  };

  // Auto-focus input fields when reaching input steps
  useEffect(() => {
    const focusInput = () => {
      switch (currentStep) {
        case 7: // Name step
          if (firstNameRef.current) {
            firstNameRef.current.focus();
          }
          break;
        case 8: // Date of birth step
          if (dateOfBirthRef.current) {
            dateOfBirthRef.current.focus();
          }
          break;
        case 9: // Email step
          if (emailRef.current) {
            emailRef.current.focus();
          }
          break;
        case 10: // Phone step
          if (phoneRef.current) {
            phoneRef.current.focus();
          }
          break;
      }
    };

    // Add a small delay to ensure the DOM is updated
    const timeoutId = setTimeout(focusInput, 100);
    return () => clearTimeout(timeoutId);
  }, [currentStep]);

  const getEstimatedTimeRemaining = () => {
    let totalMinutes = 0;
    for (let step = currentStep; step <= totalSteps; step++) {
      totalMinutes += stepTimes[step as keyof typeof stepTimes];
    }
    return totalMinutes;
  };

  const formatTime = (minutes: number) => {
    if (minutes < 1) {
      return `${Math.round(minutes * 60)}s`;
    }
    return `${Math.round(minutes)}m`;
  };

  const vehicleTypes = [
    { 
      id: 'sedan', 
      name: 'Sedan', 
      icon: '🚕'
    },
    { 
      id: 'suv', 
      name: 'SUV & Crossover', 
      icon: '🚙'
    },
    { 
      id: 'truck', 
      name: 'Truck', 
      icon: '🚚'
    },
    { 
      id: 'coupe', 
      name: 'Coupe', 
      icon: '🏎️'
    },
    { 
      id: 'hatchback', 
      name: 'Hatchback', 
      icon: '🚗'
    },
    { 
      id: 'minivan', 
      name: 'Minivan', 
      icon: '🚌'
    }
  ];

  const handleInputChange = (field: string, value: string) => {
    // Prevent multiple rapid clicks
    if (isAutoAdvancing) {
      return;
    }

    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Auto-advance for certain steps (only for selection-based steps, not text inputs)
    if (
      (field === 'vehicleType' && currentStep === 1) ||
      (field === 'monthlyBudget' && currentStep === 2) ||
      (field === 'hasTradeIn' && currentStep === 3) ||
      (field === 'employmentStatus' && currentStep === 4) ||
      (field === 'monthlyIncome' && currentStep === 5)
    ) {
      setIsAutoAdvancing(true);
      setTimeout(() => {
        setCurrentStep(prev => prev + 1);
        setIsAutoAdvancing(false);
      }, 300);
    }
  };

  const isValidPhoneNumber = (phone: string) => {
    // US and Canada phone number validation
    const phoneRegex = /^(\+1[-.\s]?)?\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})$/;
    return phoneRegex.test(phone);
  };

  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isContactFormValid = () => {
    const hasName = formData.firstName.trim() !== '';
    const hasDateOfBirth = formData.dateOfBirth !== '' && formData.dateOfBirth !== null;
    const hasAddress = formData.address.trim() !== '';
    const hasCompleteAddress = formData.address.trim().length >= 15; // Require more complete address
    const hasPhone = formData.phone.trim() !== '';
    const hasEmail = formData.email.trim() !== '';
    
    // Check honeypot fields - if filled, it's likely a bot
    if (formData.website.trim() !== '' || formData.confirmEmail.trim() !== '') {
      return false;
    }
    
    if (!hasName || !hasDateOfBirth || !hasAddress || !hasCompleteAddress) return false;
    
    // If phone is provided, it must be valid
    if (hasPhone && !isValidPhoneNumber(formData.phone)) return false;
    
    // If email is provided, it must be valid
    if (hasEmail && !isValidEmail(formData.email)) return false;
    
    // At least one contact method must be provided
    const hasContact = hasPhone || hasEmail;
    
    // Form is valid if all required fields are filled, at least one contact method is provided, and CAPTCHA is valid
    const isValid = hasContact && captchaValid;
    
    
    return isValid;
  };


  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (!isContactFormValid()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      // Prepare application data
      const applicationData: ApplicationData = {
        vehicleType: formData.vehicleType,
        monthlyBudget: formData.monthlyBudget,
        hasTradeIn: formData.hasTradeIn,
        employmentStatus: formData.employmentStatus,
        monthlyIncome: formData.monthlyIncome,
        address: formData.address,
        firstName: formData.firstName,
        dateOfBirth: formData.dateOfBirth,
        email: formData.email,
        phone: formData.phone
      };



      // Send email
      const result = await EmailService.sendApplication(applicationData);
      
      if (result.success) {
        setSubmitMessage(result.message);
        setCurrentStep(11); // Move to thank you page (step 11)
      } else {
        setSubmitMessage(result.message);
        // Stay on current step to show error message
        // Don't move to thank you page on error
      }
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitMessage('Failed to submit application. Please try again or contact us directly.');
      // Stay on current step to show error message
      // Don't move to thank you page on error
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">What type of vehicle are you looking for?</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {vehicleTypes.map((vehicle) => (
                <div
                  key={vehicle.id}
                  onClick={() => handleInputChange('vehicleType', vehicle.id)}
                  className={`cursor-pointer rounded-lg border-2 transition-all duration-200 ${
                    formData.vehicleType === vehicle.id
                      ? 'border-blue-500 bg-blue-50 shadow-lg'
                      : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                  }`}
                >
                  <div className="p-6 text-center">
                    <div className="mb-4 text-6xl">
                      {vehicle.icon}
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900">{vehicle.name}</h4>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">How much is your monthly budget?</h3>
            <div className="space-y-4">
              {[
                { value: 'under-500', label: 'Under $500' },
                { value: '500-750', label: '$500 - $750' },
                { value: '750-1000', label: '$750 - $1,000' },
                { value: '1000-plus', label: '$1,000+' }
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleInputChange('monthlyBudget', option.value)}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-all duration-200 ${
                    formData.monthlyBudget === option.value
                      ? 'border-blue-500 bg-blue-50 shadow-lg'
                      : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                  }`}
                >
                  <span className="text-lg font-medium text-gray-900">{option.label}</span>
                </button>
              ))}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Do you have a trade-in?</h3>
            <div className="space-y-4">
              {[
                { value: 'yes', label: 'Yes', icon: '✅' },
                { value: 'no', label: 'No', icon: '❌' },
                { value: 'not-sure', label: 'Not Sure', icon: '❓' }
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleInputChange('hasTradeIn', option.value)}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-all duration-200 ${
                    formData.hasTradeIn === option.value
                      ? 'border-blue-500 bg-blue-50 shadow-lg'
                      : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">{option.icon}</span>
                    <span className="text-lg font-medium text-gray-900">{option.label}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">What is your employment status?</h3>
            <div className="space-y-4">
              {[
                { value: 'employed', label: 'Employed', icon: '💼' },
                { value: 'self-employed', label: 'Self-Employed', icon: '🏢' },
                { value: 'unemployed', label: 'Unemployed', icon: '🔍' },
                { value: 'retired', label: 'Retired', icon: '🏖️' }
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleInputChange('employmentStatus', option.value)}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-all duration-200 ${
                    formData.employmentStatus === option.value
                      ? 'border-blue-500 bg-blue-50 shadow-lg'
                      : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">{option.icon}</span>
                    <span className="text-lg font-medium text-gray-900">{option.label}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">What is your monthly income?</h3>
            <div className="space-y-4">
              {[
                { value: 'under-2k', label: 'Under $2,000' },
                { value: '2k-3k', label: '$2,000 - $3,000' },
                { value: '3k-4k', label: '$3,000 - $4,000' },
                { value: '4k-5k', label: '$4,000 - $5,000' },
                { value: '5k-6k', label: '$5,000 - $6,000' },
                { value: '6k-plus', label: '$6,000+' }
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleInputChange('monthlyIncome', option.value)}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-all duration-200 ${
                    formData.monthlyIncome === option.value
                      ? 'border-blue-500 bg-blue-50 shadow-lg'
                      : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                  }`}
                >
                  <span className="text-lg font-medium text-gray-900">{option.label}</span>
                </button>
              ))}
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">What is your address?</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Full Address *</label>
                <AddressAutocomplete
                  value={formData.address}
                  onChange={(address) => handleInputChange('address', address)}
                  placeholder="Start typing your address..."
                />
                {formData.address.trim() === '' && (
                  <p className="text-red-500 text-sm mt-1">Address is required</p>
                )}
                {formData.address.trim() !== '' && formData.address.length < 15 && (
                  <p className="text-yellow-600 text-sm mt-1">
                    ⚠️ Please enter your complete address including street, city, province, and postal code
                  </p>
                )}
                <p className="text-gray-500 text-xs mt-2">
                  💡 Tip: Start typing and select from the suggestions for a complete address
                </p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> We need your address to verify your identity and determine local financing options.
                </p>
                <p className="text-sm text-blue-700 mt-2">
                  <strong>Privacy:</strong> Your address information is secure and will only be used for loan processing.
                </p>
              </div>
            </div>
          </div>
        );

      case 7:
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">What is your full name?</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                <input
                  ref={firstNameRef}
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 ${
                    formData.firstName.trim() === '' ? 'border-gray-300' : 'border-green-300'
                  }`}
                  placeholder="Enter your full name"
                  required
                />
                {formData.firstName.trim() === '' && (
                  <p className="text-red-500 text-sm mt-1">Full name is required</p>
                )}
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> Please enter your legal name as it appears on your government ID.
                </p>
              </div>
            </div>
          </div>
        );

      case 8:
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">What is your date of birth?</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date of Birth *</label>
                <input
                  ref={dateOfBirthRef}
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 ${
                    formData.dateOfBirth === '' ? 'border-gray-300' : 'border-green-300'
                  }`}
                  required
                />
                {formData.dateOfBirth === '' && (
                  <p className="text-red-500 text-sm mt-1">Date of birth is required</p>
                )}
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> We need your date of birth to verify your identity and age for loan eligibility.
                </p>
              </div>
            </div>
          </div>
        );

      case 9:
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">What is your email address?</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                <input
                  ref={emailRef}
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 ${
                    formData.email.trim() === '' 
                      ? 'border-gray-300' 
                      : isValidEmail(formData.email) 
                        ? 'border-green-300' 
                        : 'border-red-300'
                  }`}
                  placeholder="your.email@example.com"
                  required
                />
                {formData.email.trim() === '' && (
                  <p className="text-red-500 text-sm mt-1">Email address is required</p>
                )}
                {formData.email.trim() !== '' && !isValidEmail(formData.email) && (
                  <p className="text-red-500 text-sm mt-1">Please enter a valid email address</p>
                )}
                {formData.email.trim() !== '' && isValidEmail(formData.email) && (
                  <p className="text-green-500 text-sm mt-1">✓ Valid email address</p>
                )}
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> We need your email address to send you important updates about your application.
                </p>
              </div>
            </div>
          </div>
        );

      case 10:
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">What is your phone number?</h3>
            
            {/* Honeypot fields - hidden from users but visible to bots */}
            <div style={{ display: 'none' }}>
              <input
                type="text"
                name="website"
                value={formData.website}
                onChange={(e) => handleInputChange('website', e.target.value)}
                tabIndex={-1}
                autoComplete="off"
              />
              <input
                type="email"
                name="confirmEmail"
                value={formData.confirmEmail}
                onChange={(e) => handleInputChange('confirmEmail', e.target.value)}
                tabIndex={-1}
                autoComplete="off"
              />
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                <input
                  ref={phoneRef}
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 ${
                    formData.phone.trim() === '' 
                      ? 'border-gray-300' 
                      : isValidPhoneNumber(formData.phone) 
                        ? 'border-green-300' 
                        : 'border-red-300'
                  }`}
                  placeholder="(555) 123-4567 or +1-555-123-4567"
                />
                {formData.phone.trim() !== '' && !isValidPhoneNumber(formData.phone) && (
                  <p className="text-red-500 text-sm mt-1">Please enter a valid phone number</p>
                )}
                {formData.phone.trim() !== '' && isValidPhoneNumber(formData.phone) && (
                  <p className="text-green-500 text-sm mt-1">✓ Valid phone number</p>
                )}
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-700">
                  <strong>Phone formats accepted:</strong> (555) 123-4567, 555-123-4567, 555.123.4567, +1-555-123-4567
                </p>
              </div>
              
              {/* CAPTCHA */}
              <div className="mt-6">
                <SimpleCaptcha onVerify={setCaptchaValid} />
              </div>
              
              {/* Submit Button */}
              <div className="mt-8 text-center">
                <button
                  onClick={handleSubmit}
                  disabled={!isContactFormValid() || isSubmitting}
                  className={`px-8 py-3 rounded-lg font-bold transition-colors ${
                    isContactFormValid() && !isSubmitting
                      ? 'bg-green-600 text-white hover:bg-green-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {isSubmitting ? (
                    <div className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Submitting...
                    </div>
                  ) : (
                    'Submit Application'
                  )}
                </button>
                {!isContactFormValid() && (
                  <p className="text-red-500 text-sm mt-2">
                    {!captchaValid 
                      ? "Please complete the CAPTCHA verification" 
                      : "Please complete all required fields with valid information"
                    }
                  </p>
                )}
                
                {/* Display server error messages */}
                {submitMessage && !submitMessage.includes('successfully') && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-700 text-sm">
                      <strong>Error:</strong> {submitMessage}
                    </p>
                    <button
                      onClick={async () => {
                        const result = await EmailService.testApiConnection();
                        alert(`Connection Test: ${result.success ? 'SUCCESS' : 'FAILED'}\nMessage: ${result.message}`);
                      }}
                      className="mt-2 text-xs bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600"
                    >
                      Test API Connection
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 11:
        return (
          <div className="space-y-6 text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            
            <div>
              <h3 className="text-3xl font-bold text-green-600 mb-2">🎉 Congratulations!</h3>
              <p className="text-lg font-semibold text-gray-800 mb-2">
                You have been pre-approved! 
              </p>
              <p className="text-gray-600 mb-4">
                {submitMessage || 'One of our approval specialists will contact you very shortly with the best prices and rates.'}
              </p>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="text-base font-semibold text-green-900 mb-2">🚗 Your Next Steps:</h4>
              <div className="space-y-2 text-left">
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-5 h-5 bg-green-600 text-white rounded-full flex items-center justify-center text-xs font-bold mr-2 mt-0.5">1</div>
                  <p className="text-green-800 text-sm">Our approval specialist will contact you shortly</p>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-5 h-5 bg-green-600 text-white rounded-full flex items-center justify-center text-xs font-bold mr-2 mt-0.5">2</div>
                  <p className="text-green-800 text-sm">We'll discuss your personalized loan terms and rates</p>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-5 h-5 bg-green-600 text-white rounded-full flex items-center justify-center text-xs font-bold mr-2 mt-0.5">3</div>
                  <p className="text-green-800 text-sm">You could be driving your new car as soon as today!</p>
                </div>
              </div>
            </div>

            <div className="text-xs text-gray-500">
              <p>Application ID: #{Date.now().toString().slice(-6)}</p>
              <p>Submitted on: {new Date().toLocaleDateString()}</p>
              <p className="mt-2">
                Developed by{' '}
                <a 
                  href="https://www.tag4tech.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-500 hover:text-blue-400 transition-colors"
                >
                  Tag4 Technology Solutions Inc.
                </a>
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-black opacity-10"></div>
      <div className="absolute inset-0" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
      }}></div>
      
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Application Header */}
        <div className="bg-white rounded-2xl p-6 mb-8 border border-gray-200 shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Car Loan Application</h1>
              <p className="text-gray-600">Get pre-approved in minutes</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={onBackToHome}
                className="text-gray-400 hover:text-gray-600 text-2xl transition-colors"
              >
                ×
              </button>
            </div>
          </div>
          
          {/* Progress Bar - Hidden on thank you page */}
          {currentStep !== 11 && (
            <div className="mt-4">
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>Step {currentStep} of {totalSteps}</span>
                <span>Est. Time: {formatTime(getEstimatedTimeRemaining())}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${(currentStep / totalSteps) * 100}%` }}
                ></div>
              </div>
            </div>
          )}
        </div>

        {/* Application Content */}
        <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm">
          {renderStep()}
        </div>

        {/* Navigation */}
        <div className="mt-8 flex justify-between">
          {currentStep !== 11 && currentStep !== 1 ? (
            <button
              onClick={handlePrevious}
              className="flex items-center px-6 py-3 rounded-lg font-medium bg-white bg-opacity-20 text-white hover:bg-opacity-30 border border-white border-opacity-30 hover:border-opacity-50 transition-all duration-200 shadow-sm hover:shadow-md"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Previous
            </button>
          ) : (
            <div></div>
          )}
          
          {currentStep === totalSteps ? (
            <div className="text-center">
              <button
                onClick={onBackToHome}
                className="bg-yellow-400 text-blue-900 px-8 py-3 rounded-lg font-bold hover:bg-yellow-300 transition-colors"
              >
                Back to Home
              </button>
            </div>
          ) : currentStep === 1 ? (
            <div className="text-center text-blue-100 text-sm">
              {isAutoAdvancing ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </div>
              ) : (
                "Select your vehicle type to continue automatically"
              )}
            </div>
          ) : currentStep === 2 ? (
            <div className="text-center text-blue-100 text-sm">
              {isAutoAdvancing ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </div>
              ) : (
                "Select your monthly budget to continue automatically"
              )}
            </div>
          ) : currentStep === 3 ? (
            <div className="text-center text-blue-100 text-sm">
              {isAutoAdvancing ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </div>
              ) : (
                "Select your trade-in status to continue automatically"
              )}
            </div>
          ) : currentStep === 4 ? (
            <div className="text-center text-blue-100 text-sm">
              {isAutoAdvancing ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </div>
              ) : (
                "Select your employment status to continue automatically"
              )}
            </div>
          ) : currentStep === 5 ? (
            <div className="text-center text-blue-100 text-sm">
              {isAutoAdvancing ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </div>
              ) : (
                "Select your monthly income to continue automatically"
              )}
            </div>
          ) : currentStep === 6 ? (
            <div className="text-center">
              <button
                onClick={() => setCurrentStep(currentStep + 1)}
                disabled={formData.address.trim() === ''}
                className={`px-8 py-3 rounded-lg font-bold transition-colors ${
                  formData.address.trim() !== ''
                    ? 'bg-yellow-400 text-blue-900 hover:bg-yellow-300'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Next Step
              </button>
              {formData.address.trim() === '' && (
                <p className="text-red-500 text-sm mt-2">
                  Please enter your address to continue
                </p>
              )}
            </div>
          ) : currentStep === 7 ? (
            <div className="text-center">
              <button
                onClick={() => setCurrentStep(currentStep + 1)}
                disabled={formData.firstName.trim() === ''}
                className={`px-8 py-3 rounded-lg font-bold transition-colors ${
                  formData.firstName.trim() !== ''
                    ? 'bg-yellow-400 text-blue-900 hover:bg-yellow-300'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Next Step
              </button>
              {formData.firstName.trim() === '' && (
                <p className="text-red-500 text-sm mt-2">
                  Please enter your name to continue
                </p>
              )}
            </div>
          ) : currentStep === 8 ? (
            <div className="text-center">
              <button
                onClick={() => setCurrentStep(currentStep + 1)}
                disabled={formData.dateOfBirth === ''}
                className={`px-8 py-3 rounded-lg font-bold transition-colors ${
                  formData.dateOfBirth !== ''
                    ? 'bg-yellow-400 text-blue-900 hover:bg-yellow-300'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Next Step
              </button>
              {formData.dateOfBirth === '' && (
                <p className="text-red-500 text-sm mt-2">
                  Please select your date of birth to continue
                </p>
              )}
            </div>
          ) : currentStep === 9 ? (
            <div className="text-center">
              <button
                onClick={() => setCurrentStep(currentStep + 1)}
                disabled={formData.email.trim() === '' || !isValidEmail(formData.email)}
                className={`px-8 py-3 rounded-lg font-bold transition-colors ${
                  formData.email.trim() !== '' && isValidEmail(formData.email)
                    ? 'bg-yellow-400 text-blue-900 hover:bg-yellow-300'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Next Step
              </button>
              {formData.email.trim() === '' && (
                <p className="text-red-500 text-sm mt-2">
                  Please enter your email address to continue
                </p>
              )}
              {formData.email.trim() !== '' && !isValidEmail(formData.email) && (
                <p className="text-red-500 text-sm mt-2">
                  Please enter a valid email address
                </p>
              )}

            </div>
          ) : currentStep !== 11 ? (
            <button
              onClick={() => setCurrentStep(currentStep + 1)}
              className="bg-yellow-400 text-blue-900 px-8 py-3 rounded-lg font-bold hover:bg-yellow-300 transition-colors"
            >
              Next Step
            </button>
          ) : null}
        </div>
      </div>

    </div>
  );
};

export default ApplicationWorkflow;
